import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import Dock from './component/Dock';
import DockToast from './component/DockToast';
import MobileNav from './component/MobileNav';
import PixelBlast from './component/PixelBlast'; 
import GradientText from './component/GradientText';
import SpotlightCard from './component/SpotlightCard';
import DomeGallery from './component/DomeGallery';
import RotatingText from './component/RotatingText';
import {
  Home,
  Briefcase,
  Zap,
  BookOpen,
  Github,
  Linkedin,
  Mail
} from 'lucide-react'; 

function Portfolio() {
  const isMobile = window.innerWidth <= 768;
  const navigate = useNavigate();
  const dockRef = useRef(null);
  const dockItemsRef = useRef([]);
  const toastRef = useRef(null);
  const [isDockHovered, setIsDockHovered] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const pixelBlastRef = useRef(null);
  
  const dockItems = [
    { 
      icon: <Home size={18} />, 
      label: 'Home', 
      onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    { 
      icon: <Briefcase size={18} />, 
      label: 'Projects', 
      onClick: () => scrollToSection('projects')
    },
    { 
      icon: <Zap size={18} />, 
      label: 'Skills', 
      onClick: () => scrollToSection('skills')
    },
    { 
      icon: <BookOpen size={18} />, 
      label: 'Publications', 
      onClick: () => scrollToSection('publications')
    },
    // { 
    //   icon: <Github size={18} />, 
    //   label: 'GitHub', 
    //   onClick: () => window.open('https://github.com/yourusername', '_blank'),
    //   className: 'dock-divider-before'
    // },
    { 
      icon: <Linkedin size={18} />, 
      label: 'LinkedIn', 
      onClick: () => window.open('https://www.linkedin.com/in/suraj-gupta-0a7b8a183/', '_blank'),
      className: 'dock-divider-before'
    },
    { 
      icon: <Mail size={18} />, 
      label: 'Email', 
      onClick: () => window.location.href = 'mailto:suraj.gupta@euris.com'
    },
  ];

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: isMobile ? '0px 0px -20px 0px' : '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        } else {
          entry.target.classList.remove('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.fade-in, .project-card, .skill-category, .pub-card');
    animatedElements.forEach(el => observer.observe(el));

    // Hide toast and ripples when user scrolls
    const handleScroll = () => {
      if (window.scrollY > 50 && !isDockHovered) {
        setIsDockHovered(true);
        setShowToast(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isDockHovered]);

  const handleItemRefsReady = (refs) => {
    dockItemsRef.current = refs;
    requestAnimationFrame(() => {
      positionToast();
    });
  };

  const positionToast = () => {
    const projectsDockItem = dockItemsRef.current[1]; // Projects is index 1
    const toast = toastRef.current;

    if (projectsDockItem && toast) {
      const rect = projectsDockItem.getBoundingClientRect();
      const center = rect.left + (rect.width / 2);
      toast.style.left = `${center}px`;
    }
  };

  useEffect(() => {
    if (showToast) {
      // Position toast with a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        positionToast();
      }, 50);

      window.addEventListener('resize', positionToast);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', positionToast);
      };
    }
  }, [showToast]);

  useEffect(() => {
    
    if (isDockHovered) {
      setShowToast(false);
      return;
    }
    
    const timer = setTimeout(() => {
      setShowToast(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [isDockHovered]);

  // Automatic dock ripples effect
  useEffect(() => {
    
    if (isDockHovered) return; // Stop if dock has been hovered
    
    // Fire first ripple immediately
    const initialTimeout = setTimeout(() => {
      if (pixelBlastRef.current && !isDockHovered) {
        pixelBlastRef.current.triggerAutoDockRipple();
      }
    }, 500);

    const interval = setInterval(() => {
      if (pixelBlastRef.current && !isDockHovered) {
        // Trigger ripple at dock position (bottom center)
        pixelBlastRef.current.triggerAutoDockRipple();
      }
    }, 3000); // Every 3 seconds

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isDockHovered]);

  const handleDockHover = () => {
    if (!isDockHovered) {
      setIsDockHovered(true);
      setShowToast(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleViewFaStrV2 = (e) => {
    e.preventDefault();
    navigate('/projects/fastr-v2');
  };

  return (
    <div className="App">

      {/* Dock */}
      <Dock
        items={dockItems}
        panelHeight={80}
        baseItemSize={60}
        magnification={90}
        onHover={handleDockHover}
        onItemRefsReady={handleItemRefsReady}
      />

      {/* Mobile Navigation */}
      <MobileNav items={dockItems} />

      {/* Toast */}
      <DockToast ref={toastRef} isVisible={showToast} message="View my work" />

      {/* Hero */}
      <section className="hero">
        <PixelBlast
          ref={pixelBlastRef}
          variant="square"
          pixelSize={10}
          // color="#474242"
          color="#3a2828"
          patternScale={7}
          patternDensity={1.25}
          edgeFade={0.3}
          speed={0.3}
          transparent={true}
          enableRipples={true}
          rippleSpeed={0.4}
          autoDockRippleColor="#00D9FF"
        />
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <h1>
              <GradientText
                colors={["#5227FF","#FF9FFC","#B19EEF"]}
                animationSpeed={4}
                showBorder={false}
              >
                Hi, I'm Suraj
              </GradientText>
              </h1>
              <div className="hero-title-row">
              <h2>Making</h2>
              <RotatingText
                texts={['Innovative', 'Performant', 'Secure', 'Distributed']}
                // mainClassName="px-2 sm:px-2 md:px-3 bg-cyan-300 text-black overflow-hidden py-0.5 sm:py-1 md:py-2 justify-center rounded-lg"
                className='role-subtitle'
                staggerFrom={"last"}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-120%" }}
                staggerDuration={0.025}
                splitLevelClassName="overflow-hidden pb-0.5 sm:pb-1 md:pb-1"
                // transition={{ type: "bounce", damping: 50, stiffness: 400 }}
                transition={{ type: "tween", duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                rotationInterval={2000}
              />
              <h2>Solutions</h2>
              </div>
              {/* <p className="tagline">Finding business opportunities with innovative solutions</p> */}
            </div>
            <div className="hero-right">
              {/* <TextType
                text={["Industrial PhD", "Research & Development", "Blockchain & Healthcare", "Distributed Systems"]}
                typingSpeed={75}
                pauseDuration={1500}
                showCursor
                cursorCharacter="_"
                deletingSpeed={50}
                variableSpeedEnabled={false}
                variableSpeedMin={60}
                variableSpeedMax={120}
                cursorBlinkDuration={0.5}
                className='role-subtitle'
              /> */}
              <p className="hero-bio">
                Currently pursuing an Industrial PhD at Université Grenoble Alpes in collaboration
                with EURIS — researching hybrid architectures for healthcare data infrastructure that is
                both auditable and fast.
              </p>
            </div>
          </div>

          {/* <div className="hero-teaser" onClick={() => navigate('/projects/fastr-v2')}>
            <span className="teaser-project">FaSTr V2</span>
            <span className="teaser-sep">—</span>
            <span className="teaser-desc">50× performance gain in healthcare data management</span>
            <span className="teaser-badge">IEEE COMPSAC 2026</span>
          </div> */}
        </div>
      </section>

      <div className="site-body">
        <PixelBlast
          variant="square"
          pixelSize={10}
          color="#1a0a5e"
          patternScale={7}
          patternDensity={1.25}
          edgeFade={0}
          sideFade={0.3}
          speed={0.15}
          transparent={true}
          enableRipples={false}
          style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}
        />

        {/* Projects */}
        <section id="projects">
          <div className="container">
            <div className="section-header fade-in">
              <div className="section-number">01</div>
              <h2>Projects</h2>
            </div>
          <div className="projects-grid">
            <SpotlightCard className="project-card featured" spotlightColor="rgba(29, 130, 141, 0.2)">
              <span className="project-badge">FEATURED</span>
              <h3>FaSTr V2</h3>
              <p>Hybrid blockchain-based healthcare consent management system achieving upto 50× performance improvements through intelligent architecture combining Hyperledger Fabric, Neo4j, and PostgreSQL with zero-knowledge proof compliance.</p>
              <div className="tech-tags">
                <span className="tech-tag">Hyperledger Fabric</span>
                <span className="tech-tag">Neo4j</span>
                <span className="tech-tag">RISC0</span>
                <span className="tech-tag">Kubernetes</span>
              </div>
              <a href="#" className="project-link" onClick={handleViewFaStrV2}>View Details →</a>
            </SpotlightCard>

            <SpotlightCard className="project-card">
              <h3>RoboEyes</h3>
              <p>Animated eye display system on TFT SPI screens, bringing personality to robotic interfaces through smooth, expressive animations.</p>
              <div className="tech-tags">
                <span className="tech-tag">ESP32</span>
                <span className="tech-tag">SPI Display Driver</span>
                <span className="tech-tag">Animation</span>
              </div>
              <a href="https://github.com/surajguptassgg/RoboEyes" className="project-link">View Details →</a>
            </SpotlightCard>

            <SpotlightCard className="project-card">
              <h3>Blockchain as a Service for Electronic Health Records</h3>
              <p>This work presents FaSTr, a solution based on blockchain that enables Fast, Secure and Transparent access to EHRs, by providing services.</p>
              <div className="tech-tags">
                <span className="tech-tag">Hyperledger Fabric</span>
                <span className="tech-tag">Docker</span>
                <span className="tech-tag">Chaincode</span>
              </div>
              <a href="https://ieeexplore.ieee.org/document/11120521" className="project-link">Published paper →</a>
            </SpotlightCard>

            {/* <SpotlightCard className="project-card">
              spotlightColor="rgba(0, 229, 255, 0.2)"
              <h3>Project Four</h3>
              <p>Brief description of another project.</p>
              <div className="tech-tags">
                <span className="tech-tag">Tech 1</span>
                <span className="tech-tag">Tech 2</span>
              </div>
              <a href="#" className="project-link">View Details →</a>
            </SpotlightCard> */}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills">
        <div style={{width: '100vw', height: '40vh'}} className="container">
          <div className="section-header fade-in">
            <div className="section-number">02</div>
            <h2>Skills & Technologies</h2>
          </div>
          <DomeGallery
            fit={2}
            minRadius={isMobile ? 200 : 900}
            maxVerticalRotationDeg={0}
            segments={isMobile ? 35 : 25}
            dragDampening={2}
            grayscale={false}
            overlayBlurColor="#0a0a0a"
            tintColor="rgba(43, 66, 197, 0.2)"
          />
        </div>
      </section>

      {/* Publications */}
      <section id="publications">
        <div className="container">
          <div className="section-header fade-in">
            <div className="section-number">03</div>
            <h2>Publications & Research</h2>
          </div>
          
          <SpotlightCard className="pub-card">
            <h3>Scalable Blockchain-based Healthcare Consent Management with Automated Compliance Verification</h3>
            <p className="pub-meta">Suraj Gupta, Frederic Lang, Umar Ozeer, Gwen Salaün • IEEE COMPSAC • 2026</p>
            <div className="pub-links">
              <a href="#" onClick={handleViewFaStrV2}>Project Page</a>
              {/* <a href="#">Code</a> */}
              {/* <a href="#">Slides</a> */}
            </div>
          </SpotlightCard>

          <SpotlightCard className="pub-card">
            <h3>Blockchain as a service for EHRs</h3>
            <p className="pub-meta">Suraj Gupta, Frederic Lang, Umar Ozeer, Gwen Salaün • IEEE ICDH • 2025</p>
            <div className="pub-links">
              <a href="https://ieeexplore.ieee.org/document/11120521">PDF</a>
              {/* <a href="#">Code</a> */}
            </div>
          </SpotlightCard>
        </div>
      </section>
      </div>{/* site-body */}
    </div>
  );
}

export default Portfolio;
